/**
 * Query expansion utility for intelligent product search
 * Expands search queries with synonyms to improve search relevance
 */
import { SEARCH_SYNONYMS } from "./searchSynonyms";

/**
 * Validates if a query contains meaningful search terms
 * Filters out queries with only special characters or non-alphanumeric content
 */
export function isValidSearchQuery(query: string): boolean {
  if (!query || typeof query !== 'string') return false;
  
  const trimmed = query.trim();
  if (trimmed.length < 2) return false;
  
  // Check if query contains at least one alphanumeric character
  const hasAlphanumeric = /[a-zA-Z0-9]/.test(trimmed);
  if (!hasAlphanumeric) return false;
  
  // SECURITY: Reject queries that are only special characters or injection patterns
  // Using non-capturing groups and word boundaries to prevent ReDoS
  const dangerousPatterns = [
    /^[;'\"<>{}[\]()|&`$]+$/, // Only special chars
    /;\s*(?:DROP|DELETE|UPDATE|INSERT|SELECT)\b/i, // SQL injection patterns (non-capturing, word boundary)
    /\|\s*(?:cat|ls|rm|wget|curl|nc|netcat|sh|bash|python|perl|ruby|node)\b/i, // Command injection patterns
    /\$\s*\{[^}]*\}/, // Variable expansion (e.g., ${...})
    /`[^`]*`/, // Backtick command substitution
    /\$\s*\(/, // Command substitution
    /\b(?:OR|AND)\s+\d+\s*=\s*\d+\b/i, // Always true conditions (SQL injection)
    /\$ne|\$gt|\$lt|\$gte|\$lte|\$in|\$nin|\$regex|\$where|\$exists/i, // NoSQL injection operators
    /<script|javascript:|on\w+\s*=/i, // XSS patterns
    /eval\s*\(|expression\s*\(/i, // Code execution patterns
  ];
  
  for (const pattern of dangerousPatterns) {
    // SECURITY: Use test() with timeout protection to prevent ReDoS
    try {
      if (pattern.test(trimmed)) {
        return false;
      }
    } catch (error) {
      // If regex fails (unlikely), reject the query for safety
      return false;
    }
  }
  
  return true;
}

/**
 * Expands a search query by adding synonyms for each word
 * Optimized for fashion-related searches with better phrase matching
 * @param query - The raw search query string
 * @returns Array of unique search tokens (original words + synonyms)
 */
export function expandSearchQuery(query: string): string[] {
  if (!query || typeof query !== 'string') {
    return [];
  }

  const trimmed = query.trim();
  if (trimmed.length < 2) {
    return [];
  }

  const lowerQuery = trimmed.toLowerCase();
  const expanded: Set<string> = new Set();
  
  // Add the original query (preserve exact phrase)
  expanded.add(lowerQuery);

  // Split query into words (handle multi-word phrases)
  const words = lowerQuery
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return [];
  }

  // First, check for exact multi-word matches (e.g., "running shoes", "t-shirt")
  for (const [key, synonyms] of Object.entries(SEARCH_SYNONYMS)) {
    const keyLower = key.toLowerCase();
    
    // Exact phrase match (e.g., "running shoes" matches "shoes" key)
    if (lowerQuery.includes(keyLower) || keyLower.includes(lowerQuery)) {
      expanded.add(keyLower);
      synonyms.forEach(syn => expanded.add(syn.toLowerCase()));
    }
    
    // Check if any synonym phrase matches the query
    for (const synonym of synonyms) {
      const synonymLower = synonym.toLowerCase();
      if (lowerQuery.includes(synonymLower) || synonymLower.includes(lowerQuery)) {
        expanded.add(keyLower);
        synonyms.forEach(s => expanded.add(s.toLowerCase()));
        break;
      }
    }
  }

  // Then, expand individual words
  for (const word of words) {
    // Add the word itself
    expanded.add(word);
    
    // Remove hyphens for matching (e.g., "t-shirt" -> "tshirt")
    const wordNoHyphen = word.replace(/-/g, '');
    if (wordNoHyphen !== word) {
      expanded.add(wordNoHyphen);
    }
    
    // Direct synonym lookup
    if (SEARCH_SYNONYMS[word]) {
      SEARCH_SYNONYMS[word].forEach(syn => {
        expanded.add(syn.toLowerCase());
        // Also add without hyphens
        const synNoHyphen = syn.toLowerCase().replace(/-/g, '');
        if (synNoHyphen !== syn.toLowerCase()) {
          expanded.add(synNoHyphen);
        }
      });
    }
    
    // Check for partial matches in keys (e.g., "sneaker" matches "sneakers")
    for (const [key, synonyms] of Object.entries(SEARCH_SYNONYMS)) {
      const keyLower = key.toLowerCase();
      const keyNoHyphen = keyLower.replace(/-/g, '');
      
      // Check if word matches key or vice versa
      if (word === keyLower || 
          wordNoHyphen === keyNoHyphen ||
          word.includes(keyLower) || 
          keyLower.includes(word) ||
          wordNoHyphen.includes(keyNoHyphen) ||
          keyNoHyphen.includes(wordNoHyphen)) {
        expanded.add(keyLower);
        synonyms.forEach(syn => {
          expanded.add(syn.toLowerCase());
          const synNoHyphen = syn.toLowerCase().replace(/-/g, '');
          if (synNoHyphen !== syn.toLowerCase()) {
            expanded.add(synNoHyphen);
          }
        });
      }
    }
  }

  // Return unique tokens as array, limit to reasonable size to prevent performance issues
  const result = Array.from(expanded);
  
  // Limit expansion to prevent too many tokens (which could slow down search)
  // Prioritize original query and direct matches
  if (result.length > 50) {
    // Keep original query first, then limit to top 50 most relevant
    const original = result[0];
    const rest = result.slice(1).slice(0, 49);
    return [original, ...rest];
  }
  
  return result;
}

/**
 * Sanitizes and validates a search query
 * @param query - Raw query string
 * @param maxLength - Maximum allowed length (default: 100)
 * @returns Sanitized query or undefined if invalid
 */
export function sanitizeSearchQuery(query: string, maxLength: number = 100): string | undefined {
  if (!query || typeof query !== 'string') {
    return undefined;
  }

  // Trim and limit length
  let sanitized = query.trim().slice(0, maxLength);
  
  // Remove dangerous characters but allow spaces, letters, numbers, hyphens, and basic punctuation
  // Keep hyphens for terms like "t-shirt"
  sanitized = sanitized.replace(/[<>"'&;|`$(){}[\]]/g, '');
  
  // Remove SQL injection patterns
  sanitized = sanitized.replace(/;\s*(DROP|DELETE|UPDATE|INSERT|SELECT)/gi, '');
  
  // Remove command injection patterns
  sanitized = sanitized.replace(/\|\s*(cat|ls|rm|wget|curl|nc|netcat|sh|bash)/gi, '');
  
  // Final trim
  sanitized = sanitized.trim();
  
  if (sanitized.length < 2) {
    return undefined;
  }
  
  // Validate that it contains meaningful content
  if (!isValidSearchQuery(sanitized)) {
    return undefined;
  }
  
  return sanitized;
}


